import * as THREE from "three";
import {PlanetSurface} from "./planetSurface.js";
import {cubicNoise} from "../../../shaders/cubicNoise.js";
import {Random} from "../../../math/random.js";
import {Interpolation} from "../../../math/interpolation.js";

/**
 * A gas surface texture for a planet
 */
export class PlanetSurfaceGas extends PlanetSurface {
    static TEXTURE_REPEAT = 2;
    static PALETTE_SIZE = new THREE.Vector2(6, 1);
    static SHADER_BANDS_VERTEX = `
        varying vec2 iUv;
        
        void main() {
            iUv = uv;
            
            gl_Position = vec4(position, 1.);
        }
        `;
    static SHADER_BANDS_FRAGMENT = cubicNoise + `
        #define BANDS 15.
        #define SEED_MAGNITUDE 559.
        
        uniform float seed;
        uniform sampler2D palette;
    
        varying vec2 iUv;
        
        float hashBand(float v) {
            return fract(floor(cubicNoise(vec3(vec2(v * 15.), seed * SEED_MAGNITUDE)) * BANDS) * 234.411);
        }
    
        void main() {
            gl_FragColor = texture(palette, vec2(hashBand(iUv.y), 0.));
        }
        `;
    static SHADER_DISTORTION_VERTEX = PlanetSurfaceGas.SHADER_BANDS_VERTEX;
    static SHADER_DISTORTION_FRAGMENT = cubicNoise + `
        #define SCALE 20.
        #define REPEAT float(` + PlanetSurfaceGas.TEXTURE_REPEAT + `)
        #define OFFSET_X 88.
        #define OFFSET_Z 42.
        #define SEED_MAGNITUDE 389.
        
        uniform float seed;
        
        varying vec2 iUv;
        
        void main() {
            vec3 position = vec3(
                cos(iUv.x * 6.283185) * sin(iUv.y * 3.141593) * (SCALE / REPEAT) + OFFSET_X,
                cos(iUv.y * 3.141593) * SCALE + seed * SEED_MAGNITUDE,
                sin(iUv.x * 6.283185) * sin(iUv.y * 3.141593) * (SCALE / REPEAT) + OFFSET_Z);
                
            gl_FragColor = vec4(
                cubicNoise(position),
                0.,
                0.,
                1.);
        }
        `;
    static SHADER_COMPOSITE_VERTEX = PlanetSurfaceGas.SHADER_BANDS_VERTEX;
    static SHADER_COMPOSITE_FRAGMENT = `
        #define DISTORTION_AMPLITUDE .06
        #define COUNTER_RATIO 3.
        
        uniform sampler2D bands;
        uniform sampler2D distortion;
        uniform float time;
        
        varying vec2 iUv;
        
        void main() {
            vec2 offset = vec2(
                0.,
                (
                    texture(distortion, vec2(iUv.x + time * COUNTER_RATIO, iUv.y)).r *
                    texture(distortion, vec2(iUv.x + time, 1.- iUv.y)).r) * DISTORTION_AMPLITUDE);
            
            gl_FragColor = texture(bands, iUv + offset);
        }
        `;
    static TURN_SPEED = .001;
    static OUTPUT_RESOLUTION = 2048;
    static RESOLUTION_DISTORTION = new THREE.Vector2(
        PlanetSurfaceGas.OUTPUT_RESOLUTION / PlanetSurfaceGas.TEXTURE_REPEAT,
        PlanetSurfaceGas.OUTPUT_RESOLUTION);
    static RESOLUTION_BANDS = new THREE.Vector2(1, 512);
    static RESOLUTION_COMPOSITE = new THREE.Vector2(
        PlanetSurfaceGas.OUTPUT_RESOLUTION / PlanetSurfaceGas.TEXTURE_REPEAT,
        PlanetSurfaceGas.OUTPUT_RESOLUTION);

    /**
     * Render gas planet bands
     * @param {WebGLRenderer} renderer The renderer
     * @param {WebGLRenderTarget} target The render target
     * @param {Texture} texture The palette texture
     * @param {number} seed A seed
     */
    static renderBands(renderer, target, texture, seed) {
        const random = new Random(seed);
        const material = new THREE.ShaderMaterial({
            vertexShader: PlanetSurfaceGas.SHADER_BANDS_VERTEX,
            fragmentShader: PlanetSurfaceGas.SHADER_BANDS_FRAGMENT,
            uniforms: {
                palette: {
                    type: "t",
                    value: texture
                },
                seed: {value: random.float}
            }
        });
        const scene = new THREE.Scene();
        const mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material);

        scene.add(mesh);

        renderer.setRenderTarget(target);
        renderer.render(scene, new THREE.OrthographicCamera());
        renderer.setRenderTarget(null);
    }

    /**
     * Render band distortion
     * @param {WebGLRenderer} renderer The renderer
     * @param {WebGLRenderTarget} target The render target
     * @param {number} seed A seed
     */
    static renderDistortion(renderer, target, seed) {
        const random = new Random(seed);
        const material = new THREE.ShaderMaterial({
            vertexShader: PlanetSurfaceGas.SHADER_DISTORTION_VERTEX,
            fragmentShader: PlanetSurfaceGas.SHADER_DISTORTION_FRAGMENT,
            uniforms: {
                seed: {value: random.float}
            }
        });
        const scene = new THREE.Scene();
        const mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material);

        scene.add(mesh);

        renderer.setRenderTarget(target);
        renderer.render(scene, new THREE.OrthographicCamera());
        renderer.setRenderTarget(null);
    }

    /**
     * Construct a planet gas surface
     * @param {Random} random A randomizer
     * @param {number} color1 One of the gas colors in no particular order
     * @param {number} color2 One of the gas colors in no particular order
     * @param {number} color3 One of the gas colors in no particular order
     * @param {number} color4 One of the gas colors in no particular order
     * @param {number} color5 One of the gas colors in no particular order
     * @param {number} color6 One of the gas colors in no particular order
     */
    constructor(
        random,
        color1,
        color2,
        color3,
        color4,
        color5,
        color6) {
        super();

        this.turn = this.turnPrevious = 0;
        this.time = {
            value: 0
        };
        this.seedBands = random.int;
        this.seedDistortion = random.int;
        this.palette = [color1, color2, color3, color4, color5, color6];
        this.bands = new THREE.WebGLRenderTarget(
            PlanetSurfaceGas.RESOLUTION_BANDS.x,
            PlanetSurfaceGas.RESOLUTION_BANDS.y,
            {
                format: THREE.RGBFormat,
                wrapS: THREE.ClampToEdgeWrapping,
                wrapT: THREE.ClampToEdgeWrapping,
                depthBuffer: false
            });
        this.distortion = new THREE.WebGLRenderTarget(
            PlanetSurfaceGas.RESOLUTION_DISTORTION.x,
            PlanetSurfaceGas.RESOLUTION_DISTORTION.y,
            {
                format: THREE.RedFormat,
                wrapS: THREE.RepeatWrapping,
                wrapT: THREE.RepeatWrapping,
                depthBuffer: false
            });
        this.composite = new THREE.WebGLRenderTarget(
            PlanetSurfaceGas.RESOLUTION_COMPOSITE.x,
            PlanetSurfaceGas.RESOLUTION_COMPOSITE.y,
            {
                format: THREE.RGBFormat,
                wrapS: THREE.RepeatWrapping,
                wrapT: THREE.RepeatWrapping,
                depthBuffer: false
            });
        this.composite.texture.repeat.set(PlanetSurfaceGas.TEXTURE_REPEAT, 1);

        this.camera = new THREE.OrthographicCamera();
        this.compositeScene = new THREE.Scene();
        this.compositeScene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), new THREE.ShaderMaterial({
            vertexShader: PlanetSurfaceGas.SHADER_COMPOSITE_VERTEX,
            fragmentShader: PlanetSurfaceGas.SHADER_COMPOSITE_FRAGMENT,
            transparent: true,
            uniforms: {
                bands: {
                    type: "t",
                    value: this.bands.texture
                },
                distortion: {
                    type: "t",
                    value: this.distortion.texture
                },
                time: this.time
            }
        })));
    }

    /**
     * Load the planet surface
     * @param {WebGLRenderer} renderer The renderer
     * @returns {Promise} A promise that resolves when this surface is usable
     */
    load(renderer) {
        return new Promise(resolve => {
            const canvas = document.createElement("canvas");
            const context = canvas.getContext("2d");

            canvas.width = PlanetSurfaceGas.PALETTE_SIZE.x;
            canvas.height = PlanetSurfaceGas.PALETTE_SIZE.y;

            const imageData = context.createImageData(canvas.width, canvas.height);

            for (let color = 0; color < 6; ++color) {
                imageData.data[(color << 2)] = (this.palette[color] >> 16) & 0xFF;
                imageData.data[(color << 2) + 1] = (this.palette[color] >> 8) & 0xFF;
                imageData.data[(color << 2) + 2] = this.palette[color] & 0xFF;
                imageData.data[(color << 2) + 3] = 0xFF;
            }

            context.putImageData(imageData, 0, 0);

            const texture = new THREE.CanvasTexture(
                canvas,
                THREE.UVMapping,
                THREE.ClampToEdgeWrapping,
                THREE.ClampToEdgeWrapping,
                THREE.NearestFilter,
                THREE.NearestFilter);

            this.color = PlanetSurface.getAverageImageColor(context, canvas.width, canvas.height);

            PlanetSurfaceGas.renderBands(renderer, this.bands, texture, this.seedBands);
            PlanetSurfaceGas.renderDistortion(renderer, this.distortion, this.seedDistortion);

            resolve();
        });
    }

    /**
     * Get the surface texture
     * @returns {Texture} The surface texture
     */
    get texture() {
        return this.composite.texture;
    }

    /**
     * Update
     * @param {number} delta The time delta
     */
    update(delta) {
        this.turnPrevious = this.turn;

        if ((this.turn += delta * PlanetSurfaceGas.TURN_SPEED) > 1) {
            --this.turn;
            --this.turnPrevious;
        }
    }

    /**
     * Update before rendering
     * @param {WebGLRenderer} renderer The renderer
     * @param {number} time The time interpolation in the range [0, 1]
     */
    render(renderer, time) {
        this.time.value = Interpolation.lerp(this.turn, this.turnPrevious, time);

        renderer.setRenderTarget(this.composite);
        renderer.render(this.compositeScene, this.camera);
        renderer.setRenderTarget(null);
    }
}